/**
 * PulseFlow PMS - Centralized Timezone & DateTime Engine
 * 
 * Timezone Policy:
 * - Backend Exchange: UTC ISO-8601 strings (e.g. '2026-08-30T05:00:00.000Z')
 * - User Display & Business Logic: India Standard Time (IANA: 'Asia/Kolkata')
 * - Date-Only (DOB, etc.) & Time-Only (Clinic hours) preserved without UTC shifting
 * - Independent of user browser timezone settings (zero assumption on local browser offset)
 */

export const APP_TIMEZONE = 'Asia/Kolkata';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const FULL_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Dynamically computes the timezone offset in minutes for Asia/Kolkata on a specific date.
 * Does NOT hardcode +05:30.
 */
export function getTimezoneOffsetMinutes(date = new Date(), timeZone = APP_TIMEZONE) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 330; // fallback standard IST offset

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(d);
  const getPart = (type) => parseInt(parts.find((p) => p.type === type)?.value || '0', 10);

  const targetDate = new Date(Date.UTC(
    getPart('year'),
    getPart('month') - 1,
    getPart('day'),
    getPart('hour'),
    getPart('minute'),
    getPart('second')
  ));

  return (targetDate.getTime() - d.getTime()) / 60000;
}

/**
 * Parses UTC ISO timestamp or Date and extracts date/time components in Asia/Kolkata.
 */
export function utcToKolkataParts(utcInput) {
  if (!utcInput) return null;
  const d = utcInput instanceof Date ? utcInput : new Date(utcInput);
  if (isNaN(d.getTime())) return null;

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(d);
  const getVal = (type) => parts.find((p) => p.type === type)?.value || '';

  const year = parseInt(getVal('year'), 10);
  const month = parseInt(getVal('month'), 10);
  const day = parseInt(getVal('day'), 10);
  const hour24 = parseInt(getVal('hour'), 10);
  const minute = parseInt(getVal('minute'), 10);
  const second = parseInt(getVal('second'), 10);

  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

  const pad = (n) => String(n).padStart(2, '0');
  const dateString = `${year}-${pad(month)}-${pad(day)}`;
  const time24String = `${pad(hour24)}:${pad(minute)}`;
  const time12String = `${pad(hour12)}:${pad(minute)} ${period}`;

  // Weekday
  const dayIndex = new Date(Date.UTC(year, month - 1, day)).getUTCDay();

  return {
    year,
    month,
    day,
    hour24,
    hour12,
    minute,
    second,
    period,
    weekdayShort: DAY_NAMES[dayIndex],
    weekdayFull: FULL_DAY_NAMES[dayIndex],
    monthShort: MONTH_NAMES[month - 1],
    monthFull: FULL_MONTH_NAMES[month - 1],
    dateString,
    time24String,
    time12String,
    timestamp: d.getTime(),
  };
}

/**
 * Converts Indian local date ('YYYY-MM-DD') and time ('HH:mm') in Asia/Kolkata
 * to an unambiguous ISO-8601 UTC timestamp (e.g. '2026-08-30T05:00:00.000Z').
 */
export function kolkataToUtcIso(dateStr, timeStr = '00:00') {
  if (!dateStr) return '';

  let year, month, day;
  if (dateStr instanceof Date) {
    const parts = utcToKolkataParts(dateStr);
    year = parts.year;
    month = parts.month;
    day = parts.day;
  } else {
    const dateMatch = String(dateStr).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (!dateMatch) return '';
    year = parseInt(dateMatch[1], 10);
    month = parseInt(dateMatch[2], 10);
    day = parseInt(dateMatch[3], 10);
  }

  const timeMatch = String(timeStr).match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/);
  const hour = timeMatch ? parseInt(timeMatch[1], 10) : 0;
  const minute = timeMatch ? parseInt(timeMatch[2], 10) : 0;
  const second = timeMatch && timeMatch[3] ? parseInt(timeMatch[3], 10) : 0;

  // Approximate UTC date from target wall-clock
  const tentativeUtc = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const offsetMinutes = getTimezoneOffsetMinutes(tentativeUtc, APP_TIMEZONE);

  // Exact UTC instant
  const exactUtcTimestamp = tentativeUtc.getTime() - offsetMinutes * 60000;
  return new Date(exactUtcTimestamp).toISOString();
}

/**
 * Formats UTC timestamp into India Standard Time DateTime string.
 * Default: '30 Aug 2026, 10:30 AM'
 */
export function formatDateTime(utcInput, formatStr = 'DEFAULT') {
  const parts = utcToKolkataParts(utcInput);
  if (!parts) return '-';

  const pad = (n) => String(n).padStart(2, '0');

  switch (formatStr) {
    case 'FULL':
      return `${parts.weekdayFull}, ${parts.monthFull} ${parts.day}, ${parts.year} at ${parts.time12String}`;
    case 'SHORT':
      return `${parts.day} ${parts.monthShort} ${parts.year}, ${parts.time12String}`;
    case 'ISO':
      return `${parts.dateString}T${parts.time24String}:${pad(parts.second)}`;
    case 'TIME_ONLY':
      return parts.time12String;
    case 'DATE_ONLY':
      return `${parts.day} ${parts.monthShort} ${parts.year}`;
    case 'DEFAULT':
    default:
      return `${parts.day} ${parts.monthShort} ${parts.year}, ${parts.time12String}`;
  }
}

/**
 * Formats UTC timestamp into India Standard Time Date string.
 * Default: '30 Aug 2026' (or 'Sunday, August 30, 2026')
 */
export function formatDate(utcInput, formatStr = 'DEFAULT') {
  const parts = utcToKolkataParts(utcInput);
  if (!parts) return '-';

  switch (formatStr) {
    case 'FULL':
      return `${parts.weekdayFull}, ${parts.monthFull} ${parts.day}, ${parts.year}`;
    case 'LONG':
      return `${parts.monthFull} ${parts.day}, ${parts.year}`;
    case 'MONTH_DAY':
      return `${parts.monthShort} ${parts.day}`;
    case 'DAY_MONTH':
      return `${parts.day} ${parts.monthShort}`;
    case 'YYYY-MM-DD':
      return parts.dateString;
    case 'DEFAULT':
    default:
      return `${parts.day} ${parts.monthShort} ${parts.year}`;
  }
}

/**
 * Formats UTC timestamp into India Standard Time Time string.
 * E.g. '10:30 AM'
 */
export function formatTime(utcInput, hour24 = false) {
  const parts = utcToKolkataParts(utcInput);
  if (!parts) return '-';
  return hour24 ? parts.time24String : parts.time12String;
}

/**
 * Formats pure date-only string (e.g. Patient DOB '1985-05-14' or '1985-05-14T00:00:00')
 * WITHOUT converting or shifting into UTC.
 */
export function formatDateOnly(dateStr, formatStr = 'DEFAULT') {
  if (!dateStr) return '-';
  const match = String(dateStr).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!match) return String(dateStr);

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) return String(dateStr);

  const monthShort = MONTH_NAMES[month - 1] || '';
  const monthFull = FULL_MONTH_NAMES[month - 1] || '';

  switch (formatStr) {
    case 'FULL':
      return `${monthFull} ${day}, ${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    case 'DEFAULT':
    default:
      return `${day} ${monthShort} ${year}`;
  }
}

/**
 * Formats pure recurring time-only string (e.g. Clinic hours '09:00')
 * into 12-hour format '09:00 AM' WITHOUT converting to UTC.
 */
export function formatTimeOnly(timeStr) {
  if (!timeStr) return '-';
  const match = String(timeStr).match(/^(\d{1,2}):(\d{1,2})/);
  if (!match) return String(timeStr);

  const hour24 = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

  return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
}

/**
 * Returns today's calendar date string in Asia/Kolkata ('YYYY-MM-DD').
 */
export function getKolkataTodayString() {
  const parts = utcToKolkataParts(new Date());
  return parts ? parts.dateString : new Date().toISOString().split('T')[0];
}

/**
 * Returns UTC ISO string for start of day (00:00:00.000) in Asia/Kolkata.
 */
export function getKolkataStartOfDay(dateStrOrUtc = new Date()) {
  let dateStr;
  if (typeof dateStrOrUtc === 'string' && dateStrOrUtc.match(/^\d{4}-\d{2}-\d{2}$/)) {
    dateStr = dateStrOrUtc;
  } else {
    const parts = utcToKolkataParts(dateStrOrUtc);
    dateStr = parts ? parts.dateString : getKolkataTodayString();
  }
  return kolkataToUtcIso(dateStr, '00:00:00');
}

/**
 * Returns UTC ISO string for end of day (23:59:59.999) in Asia/Kolkata.
 */
export function getKolkataEndOfDay(dateStrOrUtc = new Date()) {
  let dateStr;
  if (typeof dateStrOrUtc === 'string' && dateStrOrUtc.match(/^\d{4}-\d{2}-\d{2}$/)) {
    dateStr = dateStrOrUtc;
  } else {
    const parts = utcToKolkataParts(dateStrOrUtc);
    dateStr = parts ? parts.dateString : getKolkataTodayString();
  }
  return kolkataToUtcIso(dateStr, '23:59:59');
}

/**
 * Checks if a given UTC timestamp falls on today's calendar day in Asia/Kolkata.
 */
export function isTodayInKolkata(utcInput) {
  const parts = utcToKolkataParts(utcInput);
  if (!parts) return false;
  return parts.dateString === getKolkataTodayString();
}

/**
 * Checks if a given UTC timestamp falls on tomorrow's calendar day in Asia/Kolkata.
 */
export function isTomorrowInKolkata(utcInput) {
  const parts = utcToKolkataParts(utcInput);
  if (!parts) return false;

  const todayParts = utcToKolkataParts(new Date());
  const tomorrowUtc = new Date(todayParts.timestamp + 86400000);
  const tomorrowParts = utcToKolkataParts(tomorrowUtc);

  return parts.dateString === tomorrowParts?.dateString;
}

/**
 * Generates an array of 7 consecutive calendar days (Monday to Sunday or starting from startOfWeek)
 * based strictly on Asia/Kolkata calendar dates.
 */
export function getKolkataWeekDays(referenceDateStr) {
  const refDate = referenceDateStr ? new Date(kolkataToUtcIso(referenceDateStr, '12:00:00')) : new Date();
  const parts = utcToKolkataParts(refDate);
  const todayStr = getKolkataTodayString();

  // Find Monday of the current week in Kolkata
  const dayOfWeek = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay(); // 0 is Sun
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const dayUtc = new Date(refDate.getTime() + (diffToMonday + i) * 86400000);
    const dayParts = utcToKolkataParts(dayUtc);
    weekDays.push({
      dateString: dayParts.dateString,
      dayNumber: String(dayParts.day).padStart(2, '0'),
      dayName: dayParts.weekdayShort,
      monthName: dayParts.monthShort,
      year: dayParts.year,
      isToday: dayParts.dateString === todayStr,
      fullDisplay: `${dayParts.weekdayShort}, ${dayParts.day} ${dayParts.monthShort}`,
    });
  }

  return weekDays;
}

/**
 * Calculates human-readable relative time (e.g. '5 mins ago', 'in 2 hours')
 * based on Asia/Kolkata reference.
 */
export function formatRelativeTime(utcInput) {
  if (!utcInput) return '-';
  const d = utcInput instanceof Date ? utcInput : new Date(utcInput);
  if (isNaN(d.getTime())) return '-';

  const diffMs = Date.now() - d.getTime();
  const diffSecs = Math.round(diffMs / 1000);
  const diffMins = Math.round(diffSecs / 60);
  const diffHours = Math.round(diffMins / 60);
  const diffDays = Math.round(diffHours / 24);

  if (Math.abs(diffSecs) < 60) return 'Just now';
  if (diffSecs > 0) {
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(d, 'DEFAULT');
  } else {
    const absMins = Math.abs(diffMins);
    const absHours = Math.abs(diffHours);
    if (absMins < 60) return `in ${absMins} min${absMins > 1 ? 's' : ''}`;
    if (absHours < 24) return `in ${absHours} hour${absHours > 1 ? 's' : ''}`;
    return formatDate(d, 'DEFAULT');
  }
}

export default {
  APP_TIMEZONE,
  getTimezoneOffsetMinutes,
  utcToKolkataParts,
  kolkataToUtcIso,
  formatDateTime,
  formatDate,
  formatTime,
  formatDateOnly,
  formatTimeOnly,
  getKolkataTodayString,
  getKolkataStartOfDay,
  getKolkataEndOfDay,
  isTodayInKolkata,
  isTomorrowInKolkata,
  getKolkataWeekDays,
  formatRelativeTime,
};

